import { Component, computed, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, map } from 'rxjs';

import { isNil } from '@gfe/core/utils/shared-utils';

import { ICONS } from '@core/services/icons/icons.service';
import { foldForSearch } from '@core/utils/search-text.utils';

import { FilterOperationsService } from '../filter-operations.service';
import {
  AvailableFilterField,
  FilterCondition,
  FilterDataType,
  FilterOperation,
  LabelFilterCondition
} from '../filter.models';

@Component({
  selector: 'app-add-filter-dropdown',
  standalone: true,
  imports: [TranslateModule],
  templateUrl: './add-filter-dropdown.component.html'
})
export class AddFilterDropdownComponent {
  private filterOpsService = inject(FilterOperationsService);
  private translate = inject(TranslateService);

  // Inputs
  availableFields = input.required<AvailableFilterField[]>();
  activeFilters = input<FilterCondition[]>([]);
  displayComplexFilterOption = input<boolean>(true);

  // Outputs
  addFiltering = output<FilterCondition>();
  addComplexFiltering = output<void>();
  dropdownClose = output<void>();

  // Signals
  protected searchQuery = signal('');
  protected readonly ICONS = ICONS;

  // Reactive language
  protected currentLang = toSignal(
    this.translate.onLangChange.pipe(
      filter((value) => !isNil(value)),
      map((value) => value.lang)
    ),
    { initialValue: this.translate.getCurrentLang() }
  );

  // Computed
  protected availableFieldsToShow = computed(() => {
    const fields = this.availableFields();
    const active = this.activeFilters();
    const search = foldForSearch(this.searchQuery());

    let filtered = fields.filter((field) => {
      const isActive =
        field.dataType === FilterDataType.LABELS
          ? active.some(
              (f) =>
                f.dataType === FilterDataType.LABELS &&
                (f as LabelFilterCondition).labelKey === (field.labelName || field.field)
            )
          : active.some((f) => f.field === (field.dbColumnName || field.field));
      return !isActive;
    });

    if (search) {
      filtered = filtered.filter(
        (field) =>
          foldForSearch(field.label).includes(search) ||
          foldForSearch(field.field).includes(search)
      );
    }

    return filtered;
  });

  protected updateSearch(value: string) {
    this.searchQuery.set(value);
  }

  protected addFilter(field: AvailableFilterField) {
    this.close();
    const condition = this.createDefaultCondition(field);
    this.addFiltering.emit(condition);
  }

  protected addComplexFilter() {
    this.close();
    this.addComplexFiltering.emit();
  }

  protected close() {
    this.dropdownClose.emit();
  }

  private createDefaultCondition(field: AvailableFilterField): FilterCondition {
    const baseCondition = {
      id: this.generateId(),
      field: field.dbColumnName || field.field,
      dataType: field.dataType,
      enabled: true
    };

    const operations = this.filterOpsService.getOperationsForDataType(field.dataType);
    const defaultOperation = operations[0] || FilterOperation.EQUALS;

    switch (field.dataType) {
      case FilterDataType.TEXT:
      case FilterDataType.MULTILINE_TEXT:
      case FilterDataType.LINK:
        return {
          ...baseCondition,
          operation: defaultOperation,
          value: ''
        } as FilterCondition;

      case FilterDataType.UID:
      case FilterDataType.UID_LIST:
        return {
          ...baseCondition,
          operation: defaultOperation,
          value: null,
          values: [],
          entityType: field.entityType || ''
        } as FilterCondition;

      case FilterDataType.NUMBER:
        return {
          ...baseCondition,
          operation: defaultOperation,
          value: undefined
        } as FilterCondition;

      case FilterDataType.DATE:
        return {
          ...baseCondition,
          operation: defaultOperation,
          value: undefined
        } as FilterCondition;

      case FilterDataType.DURATION:
        return {
          ...baseCondition,
          operation: defaultOperation,
          value: undefined
        } as FilterCondition;

      case FilterDataType.SELECT:
      case FilterDataType.MULTI_SELECT:
        return {
          ...baseCondition,
          operation: defaultOperation,
          values: []
        } as FilterCondition;

      case FilterDataType.BOOLEAN:
        return {
          ...baseCondition,
          operation: FilterOperation.IS_TRUE
        } as FilterCondition;

      case FilterDataType.LABELS:
        return {
          ...baseCondition,
          field: 'labels',
          operation: FilterOperation.LABEL_EQUALS,
          labelKey: field.labelName || field.field,
          labelValue: ''
        } as FilterCondition;

      case FilterDataType.TAGS:
        return {
          ...baseCondition,
          operation: FilterOperation.CONTAINS,
          tagNames: []
        } as FilterCondition;

      default:
        return {
          ...baseCondition,
          operation: defaultOperation
        } as FilterCondition;
    }
  }

  private generateId(): string {
    return `filter-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }

  protected getDataTypeIcon(dataType: FilterDataType): string {
    return this.filterOpsService.getDataTypeIcon(dataType);
  }

  protected trackByField(field: AvailableFilterField): string {
    return field.field;
  }
}
